import { useEffect, useRef, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Spinner } from '@/shared/ui/Spinner'
import { Surface } from '@/shared/ui/Surface'
import s from './LogoutSplash.module.css'

/** Tab не выводит из заставки: ни в кабинет, ни в строку адреса и оттуда обратно в кабинет. */
function keepTab(event: KeyboardEvent<HTMLDivElement>) {
  if (event.key === 'Tab') event.preventDefault()
}

/**
 * Заставка «Выходим…» поверх кабинета, пока бэк закрывает сеанс (P-AUTH-02#U-09).
 *
 * Кабинет под ней недоступен до ответа: слой ловит указатель, а фокус встаёт на заставку и
 * в кабинет не возвращается — Tab гасится, а поздний возврат фокуса снаружи (закрывшееся меню
 * аккаунта) перехватывает `focusin`. `inert` вне планки браузеров (Safari 15.5), поэтому так.
 *
 * `204` уводит на вход тем же тактом, что и сброс кеша, — заставка уходит вместе с кабинетом.
 * Сбой снимает её: кабинет остаётся как был, фокус возвращается туда, где стоял, а ошибку
 * показывает тост меню аккаунта (U-10).
 */
export function LogoutSplash() {
  const { t } = useTranslation('auth')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const splash = ref.current
    if (splash === null) return

    const previous = document.activeElement
    splash.focus()

    const keepFocus = (event: FocusEvent) => {
      if (event.target instanceof Node && !splash.contains(event.target)) splash.focus()
    }
    document.addEventListener('focusin', keepFocus)

    return () => {
      document.removeEventListener('focusin', keepFocus)
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus()
    }
  }, [])

  return createPortal(
    // Живая область и держатель фокуса — сам слой: фокус на нём читается его текстом
    <div
      ref={ref}
      className={s.root}
      role="status"
      aria-live="polite"
      tabIndex={-1}
      onKeyDown={keepTab}
    >
      <Surface variant="overlay" radius="card" elevation="modal" padding="lg" className={s.card}>
        <Spinner size="md" />
        <p className={s.text}>{t('logout.busy')}</p>
      </Surface>
    </div>,
    document.body,
  )
}
